import type React from "react";
import { Tooltip } from "@mantine/core";
import { Badge } from "@xplatform/design-system";
import type { StatusBadgeSpec } from "@utils/resultStatusBadges";

interface StatusBadgeProps {
  spec: StatusBadgeSpec;
  /** The badge's own text — short. Defaults to the spec's fallback. */
  text?: string;
  /** The full explanation: tooltip + aria-label. */
  label?: string;
  /**
   * How loud the badge is.
   *
   * `"md"` is the Status column's badge, sized to the table's own body text —
   * it IS the cell's content, so it reads at the same weight as everything
   * around it. `"sm"` is for a badge that annotates a value it sits under
   * (wd-mapping's finding, below the suggested ItemMP text): there the text is
   * the content and the badge is a footnote to it, so it drops a step rather
   * than competing with the line above.
   */
  size?: "sm" | "md";
}

/** Badge size, font size and icon size, kept in step per `size`. */
const SIZING = {
  sm: { badge: "sm", fz: "xs", icon: 12 },
  md: { badge: "md", fz: "sm", icon: 14 },
} as const;

/**
 * One status badge: an icon, a short word, and the whole story on hover.
 *
 * This replaced a bare icon. A glyph on its own has to be decoded before it can
 * be read, and the review table is scanned in bulk by someone deciding what to
 * accept. Keeping the glyph BESIDE the word inverts the roles usefully: the
 * word teaches the meaning once, the shape finds it again on the
 * five-hundredth row, and neither has to carry the job alone.
 *
 * Two type decisions worth naming, because Mantine's defaults fight both:
 *
 *  - `fz="sm"` matches the table's own body text (14px). No badge `size` does:
 *    the scale tops out at 13px for `lg` and jumps to 16px for `xl`, so the
 *    size prop sets the padding and the font size is set explicitly.
 *  - `tt="none"` because Mantine badges are UPPERCASE by default. In a 12%
 *    column holding two of these, uppercase is both wider and slower to read.
 *
 * The two texts are not interchangeable. `text` is what fits in a table cell;
 * `label` is what the reviewer actually needs and is far too long to render
 * inline. `aria-label` carries the long form, so a screen-reader user gets the
 * explanation rather than the abbreviation — but only because of the two props
 * beside it, and both are load-bearing:
 *
 *  - `role="img"` gives the badge a role at all. Mantine renders it as a plain
 *    `<div>`, and `aria-label` on a generic element with no role is ignored by
 *    most assistive tech — the label was being dropped and the two-word
 *    abbreviation announced in its place. With a role, the label becomes the
 *    accessible name and the visible text is not announced twice.
 *  - `tabIndex={0}` plus `events.focus` puts the tooltip on the keyboard path.
 *    Hover was the only way to read the explanation, which meant a keyboard
 *    user could not reach it at all.
 */
const StatusBadge: React.FC<StatusBadgeProps> = ({
  spec,
  text,
  label,
  size = "md",
}) => {
  const { Icon, color } = spec;
  const detail = label ?? spec.fallback;
  const sizing = SIZING[size];
  return (
    <Tooltip
      label={detail}
      multiline
      w={280}
      events={{ hover: true, focus: true, touch: true }}
    >
      <Badge
        size={sizing.badge}
        fz={sizing.fz}
        tt="none"
        fw={500}
        variant="light"
        color={color}
        role="img"
        aria-label={detail}
        tabIndex={0}
        leftSection={<Icon size={sizing.icon} stroke={2} aria-hidden />}
        style={{ cursor: "default" }}
      >
        {text ?? spec.badge}
      </Badge>
    </Tooltip>
  );
};

export default StatusBadge;
